// QuickAdd.js
// Component for Quickly Adding Products

// Imports
import React from 'react';
import { Tag, InputGroup, Tooltip, Position, Button } from '@blueprintjs/core';
import PropTypes from 'prop-types';

// Component Class
// Shows a product quick add bar
class QuickAdd extends React.Component {
  // Constructor
  constructor(props) {
    super(props);
    this.state = this.getEmptyState();
  }
  
  // Returns the default empty state
  getEmptyState = () => {
    return {
      text: "",
      quantity: null,
      price: null,
      product: {
        id: null,
        name: null,
        hit: false,
      },
      unit: {
        id: null,
        name: null,
        hit: false,
      }
    };
  }

  // Resets the current state
  resetState = () => {
    this.setState(this.getEmptyState());
  }

  // Returns whether str is a part of main_str while ignoring case and diacritics.
  checkString = (main_str, str) => {
    main_str = main_str.normalize('NFD').replace(/[\u0300-\u036f]/g, "")
    str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, "")
    return main_str.toLowerCase().includes(str.toLowerCase());
  }

  // Returns a table with all products whose name contains str or
  // that has a unit name containing str.
  // The returned table will be on { [id] = true } format.
  findProducts = (str) => {
    var ids = {};
    for (var i = 0; i < this.props.products.ids.length; i++) {
      var id = this.props.products.ids[i];
      var product = this.props.products.byId[id];

      if (this.checkString(product.name, str)) {
        ids[id] = true;
        continue;
      }
      for (var u = 0; u < product.price.length; u++) {
        if (this.checkString(product.price[u].unit, str)) {
          ids[id] = true;
          break;
        }
      }
    }
    return ids;
  }

  // Given a product and a list of strings, returns the target unit
  // for the given product. If it has only one unit, that will be returned.
  // If any unit contains any of the str_parts in its name it will be returned
  // as a hit. Otherwise, the first unit will be returned but won't be a hit.
  // The returned table is in format { id [number], name [string], hit [boolean] }
  findUnit = (product, str_parts) => {
    var id = 0;
    var hit = false;
    if (product.price.length === 1) {
      hit = true;
    }
    else {
      for (var u = 0; u < product.price.length; u++) {
        for (var str in str_parts) {
          if (this.checkString(product.price[u].unit, str_parts[str])) {
            id = u;
            hit = true;
            break;
          }
        }
      }
    }

    return { id: id, name: product.price[id].unit, hit: hit };
  }

  // Callback for value change on the input field
  onValueChange = (event) => {
    var text = event.target.value.trim().toLowerCase().replace(/ +(?= )/g,'');
    var parts = text.split(" ");

    var state = { text: event.target.value };

    if (text !== "") {
      // Finding the Quantity (first number)
      for (var i = 0; i < parts.length; i++) {
        var quantity = Number.parseFloat(parts[i]);
        if (quantity !== undefined && quantity >= 0) {
          state.quantity = quantity;
          parts.splice(i, 1);
          break;
        }
      }
  
      if (state.quantity === undefined)
        state.quantity = null;

      // Finding the Custom Price (second number)
      for (i = 0; i < parts.length; i++) {
        var price = Number.parseFloat(parts[i]);
        if (price && price > 0) {
          state.price = price;
          parts.splice(i, 1);
          break;
        }
      }

      if (state.price === undefined)
        state.price = null;
  
      // Finding all products that agree with the string parts
      var ids = {};
      for (i = 0; i < parts.length; i++) {
        var prods = this.findProducts(parts[i]);
        if (i === 0)
          ids = prods
        else {
          for (var key in ids) {
            if (!prods[key])
              delete ids[key];
          }
        }
      }

      // Intersecting product lists.
      // If less than 5 results, we'll show the first one.
      var list = Object.keys(ids);
      if (list.length > 0 && list.length <= 5) {
        var id = list[0];
        state.product = {
          id: id,
          name: this.props.products.byId[id].name,
          hit: list.length === 1,
        }
      }
      else {
        state.product = { id: null, name: null, hit: false };
      }

      // Finding the appropriate unit for the selected product.
      if (state.product.id) {
        var product = this.props.products.byId[state.product.id];
        state.unit = this.findUnit(product, parts);
      }
      else {
        state.unit = { id: null, name: null, hit: false };
      }

      // Setting State
      this.setState(state);
    }
    else {
      // Resetting State
      this.resetState();
    }  
  }

  // Callback for keypresses on the input field
  onKeyDown = (event) => {
    if (event.which === 13 || event.keyCode === 13) {
      if (this.addProduct())
        this.resetState();
    }
    else if (event.which === 27 || event.keyCode === 27) {
      this.resetState();
      event.preventDefault();
    }
  }

  // If all values are correctly predicted, adds the product to the list.
  addProduct = () => {
    if (this.state.quantity != null && this.state.product.id != null && this.state.unit != null) {
      this.props.setProduct(this.state.product.id, this.state.unit.id, this.state.quantity, this.state.price);
      return true;
    }
    return false;
  }

  // Returns the target intent for the tag given a state.
  // This will evaluate the 'id' and 'hit' itens of the given table.
  getTagIntent = (state) => {
    if (state.id !== null && state.hit)
      return 'success';
    else if (state.id != null)
      return 'warning';
    return undefined;
  }

  // Renders Component
  render() {
    var priceTag = (
      <Tag
        round={true}
        icon='numbered-list'
        intent={ this.state.price ? 'success' : undefined}
      >
        { this.state.price !== null ? "R$ " + this.state.price.toFixed(2) : "?" }
      </Tag>
    );
    var tags = (
      <div>
        <Tag round intent={this.getTagIntent(this.state.product)}>
          {this.state.product.id !== null ? this.state.product.name : "?"}
        </Tag>
        <Tag round intent={ this.state.quantity !== null && this.state.quantity >= 0 ? 'success' : undefined}>
          { this.state.quantity !== null ? this.state.quantity : "?" }
        </Tag>
        <Tag round intent={this.getTagIntent(this.state.unit)}>
          {this.state.unit.id !== null ? this.state.unit.name : "?"}
        </Tag>
        { this.state.price !== null ? priceTag : undefined }
      </div>
    );

    var info = (
      <div>
        A adição rápida facilita a inclusão ou substituição de novos produtos no pedido <br />
        Para isso, basta começar a digitar o nome do produto, uma quantidade e uma unidade.<br />
        Enquanto for digitando, note que as caixas à direita vão começar a tentar prever o
        que você deseja adicionar.<br />
        Elas representam, em ordem, o nome do produto, a quantidade e a unidade desejadas.<br />
        Caso estejam verdes, há apenas uma opção possível.<br />
        Caso estejam laranja, há mais de uma possibilidade e uma delas foi escolhida aleatoriamente.<br />
        <br />
        Independente da cor dos balões, caso todos os três estejam preenchidos, aperte <b>ENTER</b> para
        confirmar a adição dos produtos.<br />
        Note que, caso o produto já tenha sido adicionado antes, sua quantidade será substituída
        pelo novo valor preenchido.<br />
        Caso deseje começar de novo, aperte <b>ESC</b> para apagar a seleção atual.<br />
        <br />
        Também é possível adicionar um preço negociado, escrevendo um segundo número no campo de texto.<br />
        Um quarto balão aparecerá informando o valor negociado escolhido.<br />
        <br />
        Caso deseje remover um produto, experimente adicioná-lo com a quantidade 0.
      </div>
    )

    return (
      <table className="pt-html-table pt-html-table-striped pt-small" style={{width: '90%'}}>
        <thead>
          <tr>
            <th>
              <Tooltip content={info} position={Position.RIGHT}>
                <Button rightIcon="info-sign" small minimal>
                  <b>Adição Rápida</b>
                </Button>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <InputGroup 
                placeholder="Digite produto e quantidade para adicionar ao pedido"
                rightElement={tags}
                value={this.state.text}
                onChange={this.onValueChange}
                onKeyDown={this.onKeyDown}
              />
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
}

// Required Properties
QuickAdd.propTypes = {
  products: PropTypes.object.isRequired,
  setProduct: PropTypes.func.isRequired,
}

// Exports
export default QuickAdd;


// WEBPACK FOOTER //
// ./src/components/OrderView/QuickAdd.js